import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useNavigationStack } from '../../../context/NavigationStackContext';
import AppTabsBase, { TabConfigBase } from './AppTabsBase';

export interface TabConfig extends Omit<TabConfigBase, 'value'> {
  path: string;
  value?: string;
  additionalPaths?: string[];
  onboardingKey?: string;
}

interface AppTabsProps {
  config: TabConfig[];
  children?: React.ReactNode;
}

/** Pathname only, so tab paths with query (e.g. `/exercises/1/view/summary?fromTrainingId=x`) still match `location.pathname`. */
function pathnameOnly(path: string): string {
  const i = path.indexOf('?');
  const j = path.indexOf('#');
  if (i === -1 && j === -1) {
    return path;
  }
  const end = Math.min(i === -1 ? path.length : i, j === -1 ? path.length : j);
  return path.slice(0, end);
}

function tabSelectionKey(tab: TabConfig): string {
  return (tab.value ?? pathnameOnly(tab.path)).toLowerCase();
}

const AppTabs: React.FC<AppTabsProps> = (props) => {
  const { config, children } = props;
  const location = useLocation();
  const { replace } = useNavigationStack();
  const [currentValue, setCurrentValue] = useState(
    location.pathname.toLowerCase(),
  );

  useEffect(() => {
    const pathLower = location.pathname.toLowerCase();
    const configItem = config?.find(
      (tab) =>
        pathnameOnly(tab.path).toLowerCase() === pathLower ||
        tab?.additionalPaths
          ?.map((p) => pathnameOnly(p).toLowerCase())
          ?.includes(pathLower),
    );

    if (configItem) {
      setCurrentValue(tabSelectionKey(configItem));
    } else {
      setCurrentValue(pathLower);
    }
  }, [location, config]);

  const handleChange = (newValue: string) => {
    const item = config.find(
      (c) => tabSelectionKey(c) === newValue.toLowerCase(),
    );
    if (item) {
      replace(item.path);
    }
  };

  const baseConfig: TabConfigBase[] = config.map((c) => ({
    label: c.label,
    value: tabSelectionKey(c),
    itemsCount: c.itemsCount,
    onboardingKey: c.onboardingKey,
  }));

  return (
    <AppTabsBase
      config={baseConfig}
      value={currentValue}
      onChange={handleChange}>
      {children}
    </AppTabsBase>
  );
};

export default AppTabs;
